import { ApiError } from '@/shared/api/errors'
import { getAccessToken } from '@/shared/api/apiClient'

/**
 * Connectivity check against managerium's `/v1/hello`, resolved via Consul
 * service DNS. This is a different host than lionfish's own backend, so it
 * intentionally bypasses `apiFetch`/`API_BASE` rather than being routed
 * through it.
 */

const MANAGERIUM_BASE_URL =
  import.meta.env.VITE_MANAGERIUM_BASE_URL ??
  'https://managerium.service.consul'

export async function pingManagerium(): Promise<string> {
  const response = await fetch(`${MANAGERIUM_BASE_URL}/v1/hello`, {
    headers: { Accept: 'application/json' },
  })
  const text = await response.text()

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, text)
  }

  return text
}

/**
 * Calls managerium's real, Okta-protected `GET /v1/dashboards/{id}`
 * directly (bypassing `apiFetch`/`API_BASE`, which point at lionfish's own
 * backend) to prove the access token obtained from Okta login is accepted
 * by managerium's `JwtAuthenticationInterceptor`. Returns the raw JSON body
 * — this call exists to prove auth wiring, not to render dashboard data.
 */
export async function getManageriumDashboard(
  dashboardId: number
): Promise<unknown> {
  const accessToken = getAccessToken()
  const headers = new Headers({ Accept: 'application/json' })
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const response = await fetch(
    `${MANAGERIUM_BASE_URL}/v1/dashboards/${dashboardId}`,
    { headers }
  )
  const body = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, body)
  }

  return body
}
